<script lang="ts" setup>
import { NavigationButton } from 'monocle-components/internal'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const {
  isCurrentUserOverQuota,
  isCurrentUserNearQuota,
  shouldCurrentUserSeeFreeTrialPrompt,
  currentUser,
} = useUsers()

const showBar = computed(() => {
  return (
    isCurrentUserOverQuota.value ||
    isCurrentUserNearQuota.value ||
    shouldCurrentUserSeeFreeTrialPrompt.value
  )
})

const supportEmailLink = computed(() => {
  const userEmail = currentUser.value?.email || 'your-email@example.com'
  const userId = currentUser.value?.id || 'unknown'
  const subject = encodeURIComponent(t('account.promptBar.supportEmailSubject'))
  const body = encodeURIComponent(
    t('account.promptBar.supportEmailBody', {
      email: userEmail,
      userId,
    }),
  )
  return `mailto:support-en@monocle3d.com?subject=${subject}&body=${body}`
})
</script>

<template>
  <div
    v-if="showBar"
    class="prompt-bar"
    :class="{
      'prompt-bar--over-quota': isCurrentUserOverQuota,
      'prompt-bar--near-quota':
        isCurrentUserNearQuota && !isCurrentUserOverQuota,
    }">
    <div class="prompt-content">
      <p class="prompt-text">
        {{ isCurrentUserOverQuota ? t('account.promptBar.overQuota') : '' }}
        {{ isCurrentUserNearQuota ? t('account.promptBar.nearQuota') : '' }}

        {{
          shouldCurrentUserSeeFreeTrialPrompt
            ? t('account.promptBar.freeTrialNearlyOver')
            : ''
        }}

        🚀 {{ t('account.promptBar.upgradePrompt') }}
        <a
          :href="supportEmailLink"
          class="prompt-link"
          >{{ t('account.promptBar.contactSupport') }}</a
        >
      </p>
      <div class="prompt-actions">
        <NavigationButton
          link="/checkout"
          :label="t('account.promptBar.upgradeNow')" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.prompt-bar {
  background-color: var(--primary-color);
  width: 100%;
  padding: 12px 20px; /* Content-dependent height */
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.prompt-bar--over-quota {
  background-color: var(--upsell-banner-background);
}

.prompt-bar--near-quota {
  background-color: #ffc107; /* Yellow/amber background for near quota */
}

.prompt-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: var(--upsell-banner-text);
  font-weight: 500;
}

.prompt-text {
  margin: 0;
  line-height: 1.4;
  flex: 1;
}

.prompt-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.prompt-link {
  color: inherit;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .prompt-content {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .prompt-text {
    font-size: 0.9rem;
  }
}
</style>
